import React from "react";
import { Link } from "react-router-dom";

import { formatBytes, formatNumber } from "utils/format";
import Loading from "ui/Loading";

import { useRoutes } from "utils/routes";
import { useFeature } from "providers/OrganizationFeatures";
import LazyLoader from "ui/LazyLoader";
import Grid from "ui/Grid";
import { GridColumn, SortOrder } from "ui/Grid/util";
import {
  useSchemaIndexListQuery,
  type SchemaIndexListQuery,
} from "./gql/Query.generated";
import { useQueryStringSort } from "utils/hooks";

type SchemaIndexType = SchemaIndexListQuery["getSchemaIndices"][number];

interface Props {
  searchTerm: string | null;
  databaseId: string;
}

const FETCH_BATCH_SIZE = 50;

const Table: React.FunctionComponent<Props> = ({ searchTerm, databaseId }) => {
  const hasIndexAdvisor = useFeature("indexAdvisor");
  const [sortOpts, setSortOpts] = useQueryStringSort<keyof SchemaIndexType>({
    param: "sort",
    sortBy: "sizeBytes",
    sortDirection: "DESC",
  });

  const { data, loading, error, fetchMore } = useSchemaIndexListQuery({
    variables: {
      offset: 0,
      limit: FETCH_BATCH_SIZE,
      filter: searchTerm,
      sortBy: sortOpts.sortBy,
      sortDirection: sortOpts.sortDirection,
      databaseId,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const schemaIndices = data.getSchemaIndices;

  const fetchMoreData = (loadedCount: number) => {
    return new Promise<boolean>((resolve) => {
      const offset = loadedCount;
      fetchMore({
        variables: { offset, limit: FETCH_BATCH_SIZE },
        updateQuery: (prev, { fetchMoreResult }) => {
          const fetched = fetchMoreResult.getSchemaIndices;
          resolve(fetched.length > 0);
          if (!prev || !prev.getSchemaIndices) {
            return fetchMoreResult;
          }
          return {
            ...prev,
            getSchemaIndices: [...prev.getSchemaIndices, ...fetched],
          };
        },
      });
    });
  };

  const sortedOrder = sortOpts.sortDirection;
  const sortedBy = sortOpts.sortBy as keyof SchemaIndexType;

  function handleSort(sortBy: keyof SchemaIndexType, sortOrder: SortOrder) {
    setSortOpts({
      sortBy,
      sortDirection: sortOrder,
    });
  }

  const columns: GridColumn<SchemaIndexType, keyof SchemaIndexType>[] = [
    {
      field: "name",
      header: "Index Name",
      width: "1fr",
      renderer: function NameCell({ rowData }) {
        return (
          <NameAndIssuesCell databaseId={databaseId} schemaIndex={rowData} />
        );
      },
    },
    {
      field: "tableName",
      header: "Table Name",
      width: "1fr",
    },
    {
      field: "lastUsedAt",
      header: "Last Used",
      width: "10%",
    },
    {
      field: "sizeBytes",
      header: "Index Size",
      width: hasIndexAdvisor ? "10%" : "12%",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function IndexSizeCell({ fieldData }) {
        return formatBytes(fieldData);
      },
    },
    {
      field: "scansPerMin",
      header: "Scans / Min",
      width: "12%",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function ScansPerMinCell({ fieldData }) {
        return formatNumber(fieldData, 2);
      },
    },
    {
      field: "writeOverhead",
      header: "Write Overhead",
      width: hasIndexAdvisor ? "12%" : false,
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function WriteOverheadCell({ fieldData }) {
        return formatNumber(fieldData, 2);
      },
    },
  ];

  return (
    <LazyLoader loadedCount={schemaIndices.length} loadMore={fetchMoreData}>
      <Grid
        striped
        data={schemaIndices}
        columns={columns}
        sortedBy={sortedBy}
        sortedOrder={sortedOrder}
        handleSort={handleSort}
      />
    </LazyLoader>
  );
};

const NameAndIssuesCell: React.FunctionComponent<{
  databaseId: string;
  schemaIndex: SchemaIndexType;
}> = ({ databaseId, schemaIndex }) => {
  const { databaseIndex } = useRoutes();
  return (
    <span>
      <Link to={databaseIndex(databaseId, schemaIndex.id)}>
        {schemaIndex.name}
      </Link>
      {schemaIndex.issues.map((i) => (
        <Link
          to={databaseIndex(databaseId, schemaIndex.id)}
          className={`state-${i.severity}`}
          title={`${i.references[0].name} ${i.description}`}
          key={i.id}
        >
          <i className="icon-exclamation-sign" />
        </Link>
      ))}
    </span>
  );
};

export default Table;
